import { html, unsafeCSS, css } from 'lit';
import { property, state } from 'lit/decorators.js';
import tailwindCSS from '../../../index.css?inline';
import '../forms/form-render';
import { TemplateMixin } from '../../../core/mixins/template-mixin';
import './section-page';
import { ServicePageBase } from './base/service-page.base';
import '../adaptive-ui-icon';
import '@polymer/iron-icon/iron-icon.js';
import '@polymer/iron-icons/iron-icons.js';
import '@polymer/iron-icons/editor-icons';
import '@polymer/iron-icons/communication-icons';
import '@polymer/iron-icons/notification-icons';
import '@polymer/iron-icons/image-icons';

export class ServicePage extends TemplateMixin(ServicePageBase) {
  static readonly is = 'service-page';
  _logoError: boolean = false;
  
  static styles = [
    unsafeCSS(tailwindCSS),
    css`
      :host {
        display: block;
        width: 100%;
        height: 100%;
        font-family: 'Poppins', sans-serif;
      }

      /* Material Symbols */
      .material-symbols-outlined {
        font-family: 'Material Symbols Outlined';
        font-weight: normal;
        font-style: normal;
        font-size: 24px;
        line-height: 1;
        letter-spacing: normal;
        text-transform: none;
        display: inline-block;
        white-space: nowrap;
        word-wrap: normal;
        direction: ltr;
        -webkit-font-feature-settings: 'liga';
        -webkit-font-smoothing: antialiased;
      }

      snack-bar {
        --snackbar-success: var(--md-sys-color-primary);
        --snackbar-error: var(--md-sys-color-error);
        --snackbar-warning: var(--md-sys-color-tertiary);
        --snackbar-info: var(--md-sys-color-secondary);
        --snackbar-text: var(--md-sys-color-onPrimary);
      }

      /* ===== SNACKBAR ===== */
      .snackbar {
        position: fixed;
        bottom: 20px;
        right: 20px;
        min-width: 300px;
        max-width: 400px;
        background: var(--md-sys-color-surfaceContainerLowest);
        color: var(--md-sys-color-onSurface);
        border-radius: 8px;
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        padding: 16px 20px;
        display: flex;
        align-items: center;
        justify-content: space-between;
        gap: 12px;
        z-index: 9999;
        opacity: 0;
        transform: translateY(100%);
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        border: 1px solid var(--md-sys-color-outlineVariant);
      }
      .snackbar.active { opacity: 1; transform: translateY(0); }
      .snackbar-header { display: flex; align-items: center; gap: 12px; flex: 1; }
      .snackbar-icon { width: 20px; height: 20px; flex-shrink: 0; }
      .snackbar-content { flex: 1; min-width: 0; }
      .snackbar-title { font-weight: 600; font-size: 14px; margin-bottom: 4px; color: var(--md-sys-color-onSurface); }
      .snackbar-message { font-size: 13px; line-height: 1.4; color: var(--md-sys-color-onSurfaceVariant); }
      .snackbar-close {
        background: none; border: none; padding: 4px; cursor: pointer;
        color: var(--md-sys-color-onSurfaceVariant); border-radius: 4px;
        transition: all 0.2s ease; flex-shrink: 0;
      }
      .snackbar-close:hover { background: var(--md-sys-color-secondaryContainer); color: var(--md-sys-color-onSecondaryContainer); }
      .snackbar.success { border-left: 4px solid var(--md-sys-color-primary); }
      .snackbar.success .snackbar-icon { color: var(--md-sys-color-primary); }
      .snackbar.error { border-left: 4px solid var(--md-sys-color-error); }
      .snackbar.error .snackbar-icon { color: var(--md-sys-color-error); }
      .snackbar.warning { border-left: 4px solid var(--md-sys-color-tertiary); }
      .snackbar.warning .snackbar-icon { color: var(--md-sys-color-tertiary); }
      .snackbar.info { border-left: 4px solid var(--md-sys-color-secondary); }
      .snackbar.info .snackbar-icon { color: var(--md-sys-color-secondary); }
      .snackbar-progress {
        position: absolute; bottom: 0; left: 0; width: 100%; height: 3px;
        background: rgba(0,0,0,0.1); border-radius: 0 0 8px 8px; overflow: hidden;
      }
      .snackbar-progress-bar {
        height: 100%; background: currentColor; opacity: 0.3;
        animation: progressBar linear forwards; transform-origin: left;
      }
      @keyframes progressBar { from { transform: scaleX(1); } to { transform: scaleX(0); } }
      .snackbar { animation: snackbarSlideIn 0.3s ease-out forwards; }
      .snackbar.closing { animation: snackbarSlideOut 0.3s ease-out forwards; }
      .snackbar + .snackbar { bottom: 90px; }
      .snackbar + .snackbar + .snackbar { bottom: 160px; }
      @keyframes snackbarSlideIn { from { opacity: 0; transform: translateY(100%); } to { opacity: 1; transform: translateY(0); } }
      @keyframes snackbarSlideOut { from { opacity: 1; transform: translateY(0); } to { opacity: 0; transform: translateY(100%); } }
      @media (max-width: 640px) {
        .snackbar { left: 20px; right: 20px; min-width: auto; max-width: none; bottom: 20px; }
        .snackbar + .snackbar { bottom: 90px; }
        .snackbar + .snackbar + .snackbar { bottom: 160px; }
      }

      /* ===== DIALOG ===== */
      .dialog-backdrop {
        background-color: rgba(0,0,0,0.5);
        backdrop-filter: blur(4px);
        animation: fadeIn 0.2s ease-out;
      }
      .custom-dialog {
        animation: dialog-appear 0.3s ease-out;
        background: var(--md-sys-color-background);
        border-radius: 8px;
      }
      @keyframes dialog-appear {
        from { opacity: 0; transform: scale(0.9) translateY(-10px); }
        to { opacity: 1; transform: scale(1) translateY(0); }
      }
      @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }

      /* ===== COLLAPSED POPUP ===== */
      .group-popup {
        white-space: nowrap;
        animation: slideInRight 0.2s ease-out;
        border-radius: 8px;
      }
      @keyframes slideInRight {
        from { opacity: 0; transform: translateX(-10px); }
        to { opacity: 1; transform: translateX(0); }
      }
      .popup-menu { list-style: none; }
      .popup-item { transition: background-color 0.2s ease; border-radius: 4px; }

      /* ===== COLLAPSED POPUP LINKS ===== */
      .popup-link {
        display: block;
        padding: 10px 16px;
        font-size: 13px;
        font-weight: 500;
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-primary);
        text-decoration: none;
        border-radius: 4px;
        cursor: pointer;
        transition: background-color 0.2s ease, color 0.2s ease;
      }
      .popup-link:hover {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-onSecondaryContainer);
      }
      .popup-link.active {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-primary);
        font-weight: 600;
      }

      /* Parent icon stays highlighted while its popup is open */
      .nav-parent.popup-open {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-onSurface);
      }

      /* ===== COLLAPSE CHEVRON ===== */
      .collapse-icon { transition: transform 0.2s ease-in-out; }
      .collapse-icon.rotated { transform: rotate(180deg); }

      /* ===== SUB-MENU ANIMATION ===== */
      .sub-menu {
        transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
        max-height: 0;
        opacity: 0;
        transform: translateY(-8px);
      }
      .sub-menu.expanded {
        max-height: 500px;
        opacity: 1;
        transform: translateY(0);
      }

      /* ===== TREE LINE — reference: left:30px, 2px, subtle gray ===== */
      .tree-line { position: relative; }
      .tree-line::before {
        content: '';
        position: absolute;
        left: 30px;
        top: 4px;
        bottom: 4px;
        width: 2px;
        background-color: var(--md-sys-color-outlineVariant);
        border-radius: 2px;
        z-index: 10;
      }

      /* ===== SIDEBAR SCROLLBAR — hidden per reference ===== */
      .sidebar-scroll::-webkit-scrollbar { display: none; }
      .sidebar-scroll { -ms-overflow-style: none; scrollbar-width: none; }

      /* ===== MAIN SCROLLBAR — thin ===== */
      .main-scroll {
        scrollbar-width: thin;
        scrollbar-color: var(--md-sys-color-primary) transparent;
        overflow-y: auto;
        overflow-x: hidden;
        scroll-behavior: smooth;
      }
      .main-scroll::-webkit-scrollbar { width: 6px; height: 6px; }
      .main-scroll::-webkit-scrollbar-track { background: transparent; border-radius: 10px; margin: 4px 0; }
      .main-scroll::-webkit-scrollbar-thumb {
        background: linear-gradient(135deg, var(--md-sys-color-primary) 0%, var(--md-sys-color-primaryContainer) 100%);
        border-radius: 10px;
        border: 1px solid rgba(255,255,255,0.1);
      }
      .main-scroll::-webkit-scrollbar-corner { background: transparent; }

      /* ===== SIDEBAR TOGGLE BUTTON
         Reference: w-8 h-8 bg-primary rounded-md text-white shadow-sm ===== */
      .sidebar-toggler {
        display: flex !important;
        align-items: center !important;
        justify-content: center !important;
        cursor: pointer;
        user-select: none;
        flex-shrink: 0;
        width: 32px;
        height: 32px;
        min-width: 32px;
        min-height: 32px;
        background-color: var(--md-sys-color-primary);
        color: var(--md-sys-color-onPrimary);
        border: none;
        border-radius: 6px;
        box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        transition: opacity 0.2s ease, transform 0.1s ease;
      }
      .sidebar-toggler:hover { opacity: 0.88; }
      .sidebar-toggler:active { transform: scale(0.94); }

      /* ===== NAV PARENT
         Reference: border-left 4px solid transparent, border-radius 4px,
         color outline/on-surface-variant, font-weight 500, font-size 14px ===== */
      .nav-parent {
        border-left: 4px solid transparent;
        border-radius: 4px;
        color: var(--md-sys-color-onSurfaceVariant);
        font-weight: 500;
        font-size: 14px;
        transition: all 0.2s ease-in-out;
        width: 100%;
        display: flex;
        align-items: center;
        justify-content: space-between;
        cursor: pointer;
        background: none;
        text-decoration: none;
        box-sizing: border-box;
      }
      /* Active: primary-fixed bg, primary color + border-left, font-weight 600 */
      .nav-parent.active {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-primary);
        font-weight: 600;
        border-left-color: var(--md-sys-color-primary);
      }
      .nav-parent:not(.active):hover {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-onSurface);
      }
      /* Collapsed centering */
      .nav-parent.is-closed {
        justify-content: center !important;
        padding-left: 0 !important;
        padding-right: 0 !important;
      }

      /* ===== NAV CHILD
         Reference: border-radius 4px, color outline, font-weight 500, font-size 13px
         Active: very soft purple bg (#F4F0FF equiv via primaryFixed), primary color, bold ===== */
      .nav-child {
        border-radius: 4px;
        color: var(--md-sys-color-onSurfaceVariant);
        font-weight: 500;
        font-size: 13px;
        transition: all 0.2s ease-in-out;
        display: block;
        text-decoration: none;
        box-sizing: border-box;
        white-space: normal;
        word-break: break-word;
        line-height: 1.4;
      }
      .nav-child.active {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-primary);
        font-weight: 600;
        opacity: 0.9;
      }
      .nav-child:not(.active):hover {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-onSurface);
      }

      /* ===== LOGOUT MODAL ===== */
      .logout-modal-overlay {
        position: fixed; top: 0; left: 0; right: 0; bottom: 0;
        display: flex; align-items: center; justify-content: center;
        z-index: 10000;
        animation: fadeIn 0.2s ease-out;
      }
      .logout-modal { animation: slideUp 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
      @keyframes slideUp {
        from { opacity: 0; transform: translateY(16px) scale(0.96); }
        to { opacity: 1; transform: translateY(0) scale(1); }
      }

      /* ===== SIDEBAR COLLAPSED ICON WRAPPER ===== */
      .sidebar-closed-icon {
        width: 28px; height: 28px;
        display: flex; align-items: center; justify-content: center;
        margin: 0 auto;
      }
      .sidebar-closed-icon .iron-icon,
      .sidebar-closed-icon adaptive-ui-icon {
        width: 20px !important; height: 20px !important;
        display: flex; align-items: center; justify-content: center;
      }

      /* ===== SPINNER ===== */
      @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      .spinner { animation: spin 1s linear infinite; display: inline-block; }

      /* ===== PROFILE AREA
         Reference: flex items-center justify-between p-2 rounded-md hover:bg-surface-variant ===== */
      .profile-area {
        display: flex;
        align-items: center;
        justify-content: space-between;
        padding: 8px;
        border-radius: 6px;
        cursor: pointer;
        transition: background-color 0.2s ease;
      }

      /* Logout icon in profile */
      .logout-icon-btn {
        display: flex; align-items: center; justify-content: center;
        color: var(--md-sys-color-onSurfaceVariant);
        border-radius: 50%;
        padding: 4px;
        transition: color 0.2s ease;
        cursor: pointer;
        background: none;
        border: none;
        flex-shrink: 0;
      }
      .logout-icon-btn:hover { color: var(--md-sys-color-error); }

      /* ===== SIDEBAR LOGO AREA
         Reference: h-[60px] pl-9 pr-6 border-b ===== */
      .sidebar-logo-area {
        height: 60px;
        display: flex;
        align-items: center;
        padding-left: 36px;
        padding-right: 24px;
        border-bottom: 1px solid var(--md-sys-color-outlineVariant);
        flex-shrink: 0;
        transition: padding 0.3s ease;
      }
      .sidebar-logo-area.collapsed {
        justify-content: center;
        padding-left: 0;
        padding-right: 0;
      }

      /* ===== MOBILE SIDEBAR OVERLAY ===== */
      .mobile-overlay {
        display: none;
        position: fixed;
        inset: 0;
        background: rgba(0,0,0,0.45);
        z-index: 15;
        animation: fadeIn 0.2s ease;
      }
      .mobile-overlay.visible { display: block; }

      /* ===== THEME TOGGLE BUTTON  ===== */
      .theme-toggle-btn {
        width: 40px;
        height: 40px;
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: var(--md-sys-color-surfaceContainerLowest);
        border-radius: 9999px;
        color: var(--md-sys-color-onSurfaceVariant);
        transition: all 0.2s ease;
        border: 1px solid var(--md-sys-color-outlineVariant);
        cursor: pointer;
        flex-shrink: 0;
      }
      .theme-toggle-btn:hover {
        background-color: var(--md-sys-color-secondaryContainer);
        color: var(--md-sys-color-onSurface);
      }
      .theme-toggle-btn .material-symbols-outlined {
        font-size: 20px;
      }

      @media (max-width: 767px) {
        /* Sidebar slides in from left as overlay on mobile */
        .sidebar-nav {
          position: fixed !important;
          left: -280px !important;
          top: 0;
          bottom: 0;
          width: 260px !important;
          z-index: 20;
          transition: left 0.3s ease !important;
          /* Shadow removed as requested */
        }
        .sidebar-nav.mobile-open {
          left: 0 !important;
        }
      }

      /* ===== MISC ===== */
      @keyframes bounceIn {
        0% { opacity: 0; transform: scale(0.3); }
        50% { opacity: 1; transform: scale(1.05); }
        70% { transform: scale(0.9); }
        100% { transform: scale(1); }
      }
      .animate-bounceIn { animation: bounceIn 0.5s ease-out; }


      .setup-header {
        border-bottom: 1px solid #e5e7eb;
        display: flex;
        align-items: center;
        justify-content: space-between;
        top: 0;
        z-index: 40;
        box-shadow: 0 1px 2px rgba(0, 0, 0, 0.05);
      }

      .setup-header .brand {
        display: flex;
        align-items: center;
        gap: 0.75rem;
      }

      .setup-header .brand .logo {
        width: 32px;
        height: 32px;
        border-radius: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-weight: 700;
        font-size: 0.9rem;
        flex-shrink: 0;
      }

      .setup-header .brand .logo img {
        width: 100%;
        height: 100%;
        object-fit: contain;
        border-radius: 8px;
      }

      .setup-header .brand .title {
      font-family: 'Poppins';
      font-style: normal;
      font-weight: 400;
      font-size: 16px;
      line-height: 24px;
      /* identical to box height, or 150% */
      display: flex;
      align-items: center;
      color: #1A1B20;
      /* Inside auto layout */
      flex: none;
      }

      .setup-header .brand .title .highlight {
        color: var(--md-sys-color-primary, #1800AD);
      }

      .setup-header .right-section {
        display: flex;
        align-items: center;
        gap: 1rem;
      }

      .setup-header .institution-badge {
        font-size: 0.8rem;
    color: rgb(71, 85, 105);
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    justify-content: center;
    /* align-items: flex-start; */
    padding: 3px 10px;
    /* gap: 10px; */
    background: rgb(255, 255, 255);
    border: 1px solid rgb(196, 198, 210);
    border-radius: 9999px;
    /* flex: 0 0 auto; */
    /* order: 1; */
    align-self: center;
      }

      .setup-header .institution-badge .icon {
        font-size: 0.9rem;
      }

      .setup-header .institution-badge .name {
        font-weight: 500;
        color: #1a1a2e;
      }

      .setup-header .powered-by {
        display: flex;
        align-items: center;
        gap: 0.4rem;
        font-size: 0.7rem;
        letter-spacing: 0.02em;
          font-family: 'Poppins';
          font-style: normal;
          font-weight: 500;
          font-size: 12px;
          line-height: 13px;
          /* identical to box height, or 110% */
          display: flex;
          align-items: center;
          color: var(--md-sys-color-primary, #1800AD);
          /* Inside auto layout */
          flex: none;
          order: 0;
          flex-grow: 0;


        
      }

      .setup-header .powered-by .brand-name {
        font-weight: 600;
        color: #64748b;
      }

      /* Mobile responsive */
      @media (max-width: 640px) {
        .setup-header {
          padding: 0.5rem 1rem;
          flex-wrap: wrap;
          gap: 0.5rem;
        }

        .setup-header .brand .title {
          font-size: 0.85rem;
        }

        .setup-header .brand .logo {
          width: 28px;
          height: 28px;
          font-size: 0.75rem;
        }

        .setup-header .institution-badge {
          padding: 0.3rem 0.75rem;
          font-size: 0.7rem;
        }

        .setup-header .right-section {
          gap: 0.5rem;
        }

        .setup-header .powered-by {
          font-size: 0.6rem;
        }
      }

      @media (max-width: 480px) {
        .setup-header {
          flex-direction: column;
          align-items: stretch;
          gap: 0.4rem;
          padding: 0.5rem 0.75rem;
        }

        .setup-header .brand {
          justify-content: center;
        }

        .setup-header .right-section {
          justify-content: center;
          flex-wrap: wrap;
        }
      }

      /* Animation */
      @keyframes slideDown {
        from {
          transform: translateY(-100%);
          opacity: 0;
        }
        to {
          transform: translateY(0);
          opacity: 1;
        }
      }

      .setup-header {
        animation: slideDown 0.3s ease forwards;
      }

      .institution-name{
      /* Frame 1171280208 */
        /* Auto layout */
        display: flex;
        flex-direction: row;
        align-items: center;
        padding: 0px;
        gap: 8px;
        /* Inside auto layout */
        /* Overlay+Border */
        box-sizing: border-box;
        background: rgba(0, 105, 92, 0.08);
        border: 1px solid rgba(0, 105, 92, 0.18);
        border-radius: 9999px;
        font-family: 'Poppins';
        font-style: normal;
        font-weight: 600;
        font-size: 10px;
        line-height: 15px;
        /* identical to box height, or 150% */
        display: flex;
        align-items: center;
        letter-spacing: 0.5px;
        text-transform: uppercase;
        color: #00695C;




        }

    `
  ];

  @property({ type: Object })
  interfaceData: any = null;

  @property({ type: String }) institutionName = 'Institution';


  @property({ type: Object })
  pageGroup: any = null;

  @property({ type: Object })
  gateway: any = null;

  @property({ type: String })
  view: string = 'main';

  @state()
  private sidebarOpen: boolean = false;
  @state() private _theme: 'light' | 'dark' = 'light';
  @state()
  private expandedGroupIndex: number | null = null;

  @state()
  private showGroupPopup: number | null = null;

  @property({ type: String })
  gatewayName: string = '';

  @property({ type: String })
  names: string = '';

  @property({ type: String })
  accessLevel: string = '';

  @state()
  private _pageGroupSlug: string = '';

  @state()
  private _pageSlug: string = '';

  @property({ type: String })
  logo = '';

  @state()
  private showLogoutConfirmation: boolean = false;

  @state() private _snackbarOpened: boolean = false;
  @state() private _snackbarTitle: string = '';
  @state() private _snackbarMessage: string = '';
  @state() private _snackbarContext: string = 'info'; // 'info', 'success', 'error', etc.
  @state() private logoutLoading: boolean = false;
  @state() private _snackbarTimeout: any = null;

  // Mobile-specific overlay sidebar state
  @state() private _mobileSidebarOpen: boolean = false;
  
  // Track previous viewport to detect desktop to mobile switch
  private _wasMobile: boolean = false;

  connectedCallback() {
    super.connectedCallback();
    this._initializeTheme();
    this._parseCurrentHash();
    window.addEventListener('hashchange', this._parseCurrentHash.bind(this));
    this._loadFonts();
    window.addEventListener('resize', this._onResize.bind(this));
    
    // Auto-open sidebar on mobile when component loads
    this._handleMobileSidebarOnLoad();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('hashchange', this._parseCurrentHash.bind(this));
    window.removeEventListener('resize', this._onResize.bind(this));
  }

  firstUpdated() {
    // Auto-open sidebar on mobile after first render
    this._handleMobileSidebarOnLoad();
  }

  private _onResize() {
    const isMobile = this._isMobile;
    
    // Detect when switching from desktop to mobile
    if (!this._wasMobile && isMobile) {
      // Switched from desktop to mobile - auto-open sidebar
      this._mobileSidebarOpen = true;
      this.requestUpdate();
    } 
    // Detect when switching from mobile to desktop
    else if (this._wasMobile && !isMobile) {
      // Switched from mobile to desktop - close mobile sidebar overlay
      this._mobileSidebarOpen = false;
      this.requestUpdate();
    }
    
    // Update the previous mobile state
    this._wasMobile = isMobile;
  }

  private _handleMobileSidebarOnLoad() {
    // Auto-open sidebar on mobile devices on initial load or refresh
    if (this._isMobile) {
      this._mobileSidebarOpen = true;
      this._wasMobile = true;
      this.requestUpdate();
    } else {
      this._wasMobile = false;
    }
  }

  private _loadFonts() {
    if (!document.querySelector('link[href*="Poppins"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&display=swap';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
    if (!document.querySelector('link[href*="Material+Symbols"]')) {
      const link = document.createElement('link');
      link.href = 'https://fonts.googleapis.com/css2?family=Material+Symbols+Outlined:opsz,wght,FILL,GRAD@24,400,0,0';
      link.rel = 'stylesheet';
      document.head.appendChild(link);
    }
  }

  private _parseCurrentHash() {
    const hash = window.location.hash.substring(2);
    const parts = hash.split('/');
    if (parts.length >= 2) {
      this._pageGroupSlug = parts[0];
      this._pageSlug = parts[1];
    
    // Find and expand the correct group
      if (this.interface?.pageGroups) {
        const groupIndex = this.interface.pageGroups.findIndex((group: any) =>
          ServicePage.slugify(group.title) === this._pageGroupSlug
        );
        if (groupIndex !== -1) this.expandedGroupIndex = groupIndex;
      }
      this.requestUpdate();
    }
  }

private _initializeTheme() {
  const savedTheme = localStorage.getItem('dark_mode') as 'light' | 'dark';
  const theme = savedTheme || 'light';
  this._theme = theme;              // <-- add this
  this._applyTheme(theme);
  this.requestUpdate();
}

  updated(changedProperties: Map<string | number | symbol, unknown>) {
    if (changedProperties.has('view')) {
      this.dialogOpen = this.view === 'dialog';
      if (this.view === 'dialog') {
        document.body.classList.add('dialog-open');
      } else {
        document.body.classList.remove('dialog-open');
      }
      this.requestUpdate();
    }
  }

  private get _isMobile(): boolean {
    return window.innerWidth < 768;
  }

  private toggleSidebar() {
    if (this._isMobile) {
      this._mobileSidebarOpen = !this._mobileSidebarOpen;
    } else {
      this.sidebarOpen = !this.sidebarOpen;
      if (!this.sidebarOpen) {
        this.expandedGroupIndex = null;
        this.showLogoutPopover = false;
        this.showGroupPopup = null;
      }
    }
    this.requestUpdate();
  }

  private _closeMobileSidebar() {
    this._mobileSidebarOpen = false;
    this.requestUpdate();
  }

  private toggleGroup(index: number) {
    if (this.sidebarOpen || this._isMobile) {
      this.expandedGroupIndex = this.expandedGroupIndex === index ? null : index;
    } else {
      this.showGroupPopup = this.showGroupPopup === index ? null : index;
    }
    this.requestUpdate();
  }

  private menuItemClickHandler(_event: Event) {
    this.showLogoutPopover = false;
    this.showGroupPopup = null;
    if (this._isMobile) {
      this._mobileSidebarOpen = false;
    }
  }

  private closeDialog() {
    this.hideSectionPage();
    this.dispatchEvent(new CustomEvent('view-change', {
      bubbles: true, composed: true, detail: { view: 'main' }
    }));
  }

  private async logout() {
    this.showLogoutConfirmation = true;
  }

  private cancelLogout() {
    this.showLogoutConfirmation = false;
    this.logoutLoading = false;
    this.requestUpdate();
  }

_viewList(_evt: any) {
  const detail = _evt?.detail || {};

  // If this was an error close, show snackbar and tear down dialog
  if (detail.error) {
    this._showSnackbar(detail.error, 'error');

    // Close the dialog entirely
    this.hideSectionPage?.();
    this.dialogsStack = [];
    this.view = 'main';
    this.dialogOpen = false;
    document.body.classList.remove('dialog-open');
    this.requestUpdate();
    return;
  }

  if (!this.dialogsStack.length) {
    this.view = 'list';
    this.updateLocationHash();
  } else {
    const args = this.dialogsStack.pop();
    const dialog = this.shadowRoot?.querySelector('#dialog');
    if (dialog) {
      dialog.payload = args[0];
      dialog.params = args[1];
      dialog.loading = false;
    }
  }
}

  static slugify(str: string): string {
    return str.toLowerCase().replace(/\s+/g, '-');
  }

  static toTitleCase(str: string): string {
    return str.replace(/\w\S*/g, (txt) =>
      txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
    );
  }

  private _getSubMenuHeight(index: number): string {
    const pageCount = this.interface?.pageGroups?.[index]?.pages?.length || 0;
    return `${pageCount * 56}px`;
  }

private toggleTheme() {
  const newTheme = this._theme === 'dark' ? 'light' : 'dark';
  this._theme = newTheme;
  localStorage.setItem('dark_mode', newTheme);   // ✅ match the read key
  this._applyTheme(newTheme);
  this.dispatchEvent(new CustomEvent('theme-changed', {
    bubbles: true, composed: true, detail: { theme: newTheme }
  }));
  this.requestUpdate();
}

  private _applyTheme(theme: 'light' | 'dark') {
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
    this._reapplyMaterialTheme(theme);
  }

  private async _reapplyMaterialTheme(_theme: 'light' | 'dark') {
    this.requestUpdate();
  }

  private getProfileInitials(): string {
    if (this.gateway?._response?.profile?.first_name) {
      const firstInitial = this.gateway._response.profile.first_name.charAt(0).toUpperCase();
      const lastInitial = this.gateway._response.profile.last_name
        ? this.gateway._response.profile.last_name.charAt(0).toUpperCase() : '';
      return `${firstInitial}${lastInitial}`;
    }
    return 'UN';
  }

  private _showSnackbar(message: string, context: string = 'info') {
    this._closeSnackbar();
    this._snackbarTitle = context.charAt(0).toUpperCase() + context.slice(1);
    this._snackbarMessage = message;
    this._snackbarContext = context;
    this._snackbarOpened = true;
    if (this._snackbarTimeout) clearTimeout(this._snackbarTimeout);
    this._snackbarTimeout = setTimeout(() => this._closeSnackbar(), 5000);
    this.requestUpdate();
  }

  private _closeSnackbar() {
    if (this._snackbarOpened) {
      this._snackbarOpened = false;
      if (this._snackbarTimeout) { clearTimeout(this._snackbarTimeout); this._snackbarTimeout = null; }
      this.requestUpdate();
    }
  }

  async confirmLogout(evt: { preventDefault: () => void; }) {
    evt.preventDefault();
    if (this.logoutLoading) return;
    this.logoutLoading = true;
    try {
      this._showSnackbar('Logging out...', 'info');
      const response = await fetch('/logout', {
        method: 'POST',
        headers: { 'X-CSRFToken': (window as any).csrfToken }
      });
      if (response.ok) {
        window.location.href = '/';
      } else {
        this._showSnackbar('Logout failed. Please try again.', 'error');
        this.logoutLoading = false;
        this.showLogoutConfirmation = false;
      }
    } catch (error) {
      console.error('Logout error:', error);
      this._showSnackbar('Network error. Please try again.', 'error');
      this.logoutLoading = false;
      this.showLogoutConfirmation = false;
    }
    this.requestUpdate();
  }


  render() {
    if (!this.interfaceData || !this.pageGroup) {
      return html`
        <div class="flex items-center justify-center h-full min-h-screen bg-gradient-to-br from-slate-50 to-gray-100">
          <div class="text-center p-8 max-w-md">
            <div class="relative mb-6">
              <div class="w-20 h-20 mx-auto bg-gradient-to-r from-rose-400 to-pink-500 rounded-2xl flex items-center justify-center shadow-lg shadow-pink-500/25 rotate-45 transform-gpu">
                <svg class="w-10 h-10 text-white -rotate-45" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </div>
              <div class="absolute -inset-2 bg-rose-500/10 rounded-3xl blur-sm animate-pulse"></div>
            </div>
            <h2 class="text-2xl font-bold text-gray-900 mb-3">Content Unavailable</h2>
            <p class="text-gray-600 mb-6 leading-relaxed">
              We couldn't load the interface data at this moment. Please try refreshing the page.
            </p>
            <button onclick="window.location.href = '/';"
              class="group relative inline-flex items-center px-6 py-3 bg-gradient-to-r from-slate-900 to-gray-800 text-white font-medium rounded-xl hover:from-gray-800 hover:to-slate-900 transition-all duration-300 shadow-lg hover:shadow-xl hover:scale-105 transform-gpu">
              <svg class="w-5 h-5 mr-2 group-hover:rotate-180 transition-transform duration-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"/>
              </svg>
              Go to Homepage
            </button>
          </div>
        </div>
      `;
    }

    const isDarkMode = this._theme === 'dark';
    const isMobile = this._isMobile;
    const showSidebarExpanded = this.sidebarOpen || isMobile;
    const sidebarWidth = showSidebarExpanded ? '260px' : '80px';

    return html`
      <div class="flex flex-col h-screen bg-[var(--md-sys-color-surfaceContainerLow)] transition-colors duration-200"
           style="font-family: 'Poppins', sans-serif;">
        
        <!-- ===========================
             TOP BAR
             Layout: [logo zone | vertical divider | toggle + breadcrumb --- theme toggle]
        =========================== -->
        ${this.gateway?._response?.instititution_setup ? html`

          <header class="flex items-center border-b border-[var(--md-sys-color-outlineVariant)] bg-[var(--md-sys-color-surfaceContainerLowest)] shrink-0 transition-colors duration-200 z-10 setup-header" style="height: 60px;">
          
          <!-- Logo zone: matches sidebar width, no horizontal padding on header itself -->
          <div
            class="flex items-center justify-center shrink-0 transition-all duration-300 ease-in-out h-full"
            style="width: ${isMobile ? '260px' : sidebarWidth};"
          >
            ${showSidebarExpanded ? html`
              <img
                alt="logo"
                class="object-contain"
                style="height: 36px; width: auto;"
                src=${this.logo}
                @error=${() => this._logoError = true}
              >
            ` : html`
              <img
                alt="N"
                class="object-contain"
                style="height: 36px; width: 36px; border-radius: 6px;"
                src=${this.logo}
                @error=${() => this._logoError = true}
              >
            `}
          </div>

          <!-- Vertical divider -->
          <div class="h-full w-px shrink-0 bg-[var(--md-sys-color-outlineVariant)]"></div>

          <!-- Right section: toggle + breadcrumb + spacer + theme toggle -->
          <div class="flex items-center flex-1 h-full min-w-0" style="padding: 0 16px; gap: 12px;">

            <!-- Sidebar Toggle Button -->
            <button
              @click=${this.toggleSidebar}
              class="sidebar-toggler w-8 h-8 flex items-center justify-center bg-[var(--md-sys-color-primary)] rounded-md text-[var(--md-sys-color-onPrimary)] transition-colors shadow-sm hover:opacity-90 focus:outline-none shrink-0"
              aria-label="${this.sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}"
              title="${this.sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}"
            >
              <span class="material-symbols-outlined text-[20px]">
                ${this.sidebarOpen ? 'keyboard_double_arrow_left' : 'keyboard_double_arrow_right'}
              </span>
            </button>

            <!-- Breadcrumb -->
            <p class="text-base font-normal text-[var(--md-sys-color-onSurface)] transition-colors duration-200 truncate m-0">
             <div class="brand">
          
          

          

          <span class="institution-name">
              <div style="width:8px;height:8px;background: #00695C;">
              </div>
            <!--?lit$261627351$-->Institution
          </span>
          
        </div>
            </p>

            <div class="flex-grow"></div>

            <!-- Theme Toggle-->
             <div class="institution-badge">
            <div class="powered-by">
            Powered by
            <img src="/static/dsv4.0/images/web/kenswitch.png" alt="kenswitch" />
          </div>
          </div>
        </header>

          ` : html` 
        
        <header class="flex items-center border-b border-[var(--md-sys-color-outlineVariant)] bg-[var(--md-sys-color-surfaceContainerLowest)] shrink-0 transition-colors duration-200 z-10" style="height: 60px;">
          
          <!-- Logo zone: matches sidebar width, no horizontal padding on header itself -->
          <div
            class="flex items-center justify-center shrink-0 transition-all duration-300 ease-in-out h-full"
            style="width: ${isMobile ? '260px' : sidebarWidth};"
          >
            ${showSidebarExpanded ? html`
              <img
                alt="logo"
                class="object-contain"
                style="height: 36px; width: auto;"
                src=${this.logo}
                @error=${() => this._logoError = true}
              >
            ` : html`
              <img
                alt="N"
                class="object-contain"
                style="height: 36px; width: 36px; border-radius: 6px;"
                src=${this.logo}
                @error=${() => this._logoError = true}
              >
            `}
          </div>

          <!-- Vertical divider -->
          <div class="h-full w-px shrink-0 bg-[var(--md-sys-color-outlineVariant)]"></div>

          <!-- Right section: toggle + breadcrumb + spacer + theme toggle -->
          <div class="flex items-center flex-1 h-full min-w-0" style="padding: 0 16px; gap: 12px;">

            <!-- Sidebar Toggle Button -->
            <button
              @click=${this.toggleSidebar}
              class="sidebar-toggler w-8 h-8 flex items-center justify-center bg-[var(--md-sys-color-primary)] rounded-md text-[var(--md-sys-color-onPrimary)] transition-colors shadow-sm hover:opacity-90 focus:outline-none shrink-0"
              aria-label="${this.sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}"
              title="${this.sidebarOpen ? 'Collapse sidebar' : 'Expand sidebar'}"
            >
              <span class="material-symbols-outlined text-[20px]">
                ${this.sidebarOpen ? 'keyboard_double_arrow_left' : 'keyboard_double_arrow_right'}
              </span>
            </button>

            <!-- Breadcrumb -->
            <p class="text-base font-normal text-[var(--md-sys-color-onSurface)] transition-colors duration-200 truncate m-0">
              ${this.gateway?._response.name} / ${this.pageGroup.title} / ${this.page?.title || 'Loading...'}
            </p> 

            <div class="flex-grow"></div>

            <!-- Theme Toggle-->
            <button
              @click=${this.toggleTheme}
              class="theme-toggle-btn"
              title="Toggle Dark/Light Mode"
            >
              <span class="material-symbols-outlined">
                ${isDarkMode ? 'light_mode' : 'dark_mode'}
              </span>
            </button>
          </div>
        </header> `}

        <!-- ===========================
             MAIN BODY
        =========================== -->
        <div class="flex flex-1 overflow-hidden relative">

          <!-- Mobile overlay backdrop -->
          <div
            class="mobile-overlay ${isMobile && this._mobileSidebarOpen ? 'visible' : ''}"
            @click=${this._closeMobileSidebar}
          ></div>

          <!-- ===========================
               SIDEBAR
          =========================== -->
          <nav
            class="sidebar-nav flex flex-col h-full border-r border-[var(--md-sys-color-outlineVariant)] bg-[var(--md-sys-color-surfaceContainerLowest)] transition-[width] duration-300 ease-in-out overflow-x-hidden shrink-0
                   ${isMobile ? (this._mobileSidebarOpen ? 'mobile-open' : '') : ''}"
            style="${isMobile ? 'width: 260px;' : `width: ${sidebarWidth};`}"
          >

            <!-- Nav Links -->
            <ul
              class="flex-1 overflow-y-auto sidebar-scroll m-0 list-none"
              style="padding: 24px 16px; display: flex; flex-direction: column; gap: 8px;"
            >
              ${(this.interface?.pageGroups || []).map((pageGroup: any, index: number) => {
                const groupSlug = ServicePage.slugify(pageGroup.title);
                const isActiveGroup = groupSlug === this._pageGroupSlug;
                const isExpanded = this.expandedGroupIndex === index;
                const showExpanded = showSidebarExpanded;

                return html`
                  <li style="display: flex; flex-direction: column;">

                    <!-- Nav Parent -->
                    <a
                      class="nav-parent ${isActiveGroup ? 'active' : ''} ${!showExpanded ? 'is-closed' : ''} ${!showExpanded && this.showGroupPopup === index ? 'popup-open' : ''}"
                      style="padding: ${showExpanded ? '10px 16px' : '10px 0'};"
                      title=${!showExpanded ? ServicePage.toTitleCase(pageGroup.title) : ''}
                      aria-label=${ServicePage.toTitleCase(pageGroup.title)}
                      @click=${() => this.toggleGroup(index)}
                    >
                      <span style="display:flex; align-items:${showExpanded ? 'center' : 'center'}; gap: 12px;">
                        <!-- Icon: 20px shrink-0 -->
                        <span class="${!showExpanded ? 'sidebar-closed-icon' : ''}"
                              style="flex-shrink:0; ${showExpanded ? 'width:20px;height:20px;display:flex;align-items:center;justify-content:center;' : ''}">
                          ${pageGroup.icon ? html`
                            <iron-icon
                              class="iron-icon"
                              style="width:20px !important;height:20px !important;display:block;"
                              icon="${pageGroup.icon}"
                            ></iron-icon>
                          ` : html`
                            <adaptive-ui-icon icon="MdInfo"
                              style="display:flex;align-items:center;justify-content:center;width:20px;height:20px;">
                            </adaptive-ui-icon>
                          `}
                        </span>
                        ${showExpanded ? html`
                          <span style="font-size:14px; line-height:1.35; text-align:left;">
                            ${ServicePage.toTitleCase(pageGroup.title)}
                          </span>
                        ` : ''}
                      </span>
                      ${showExpanded && pageGroup.pages?.length ? html`
                        <span class="collapse-icon ${isExpanded ? 'rotated' : ''}"
                              style="flex-shrink:0; display:flex; align-items:center;">
                          <adaptive-ui-icon icon="icons:expand-more"></adaptive-ui-icon>
                        </span>
                      ` : ''}
                    </a>

                    <!-- Sub-menu: pl-[44px] pr-2 py-1, tree-line pseudo, gap-1 between children -->
                    ${showExpanded && pageGroup.pages?.length ? html`
                      <div
                        class="sub-menu tree-line ${isExpanded ? 'expanded' : ''}"
                        style="padding-left:44px; padding-right:8px;
                        display:flex; flex-direction:column; gap:4px;
                        ${isExpanded
                          ? `padding-top:4px; padding-bottom:4px; margin-top:4px; max-height:${this._getSubMenuHeight(index)};`
                          : 'padding-top:0; padding-bottom:0; margin-top:0;'}"
                      >
                        ${pageGroup.pages.map((menu: any) => {
                          const pageSlug = ServicePage.slugify(menu.title);
                          const isActive = groupSlug === this._pageGroupSlug && pageSlug === this._pageSlug;
                          return html`
                            <a
                              class="nav-child ${isActive ? 'active' : ''}"
                              style="padding: 10px 16px;"
                              @click=${this.menuItemClickHandler}
                              href="${window.location.pathname + window.location.search}#/${groupSlug}/${pageSlug}/"
                            >
                              ${ServicePage.toTitleCase(menu.title)}
                            </a>
                          `;
                        })}
                      </div>
                    ` : ''}

                    <!-- Collapsed popup (desktop only) -->
                    ${!showExpanded && pageGroup.pages?.length && this.showGroupPopup === index ? html`
                      <div
                        class="group-popup absolute bg-[var(--md-sys-color-surfaceContainerLowest)] border border-[var(--md-sys-color-outlineVariant)] p-2 shadow-lg z-50"
                        style="left: calc(80px + 8px); top: calc(60px + ${index * 44}px + 24px); min-width: 200px;"
                      >
                        <ul class="popup-menu p-0 m-0" style="list-style:none; display:flex; flex-direction:column; gap:2px;">
                          ${pageGroup.pages.map((menu: any) => {
                            const pageSlug = ServicePage.slugify(menu.title);
                            const isActive = groupSlug === this._pageGroupSlug && pageSlug === this._pageSlug;
                            return html`
                              <li class="popup-item">
                                <a
                                  class="popup-link ${isActive ? 'active' : ''}"
                                  @click=${(e: Event) => {
                                    this.menuItemClickHandler(e);
                                    this.showGroupPopup = null;
                                  }}
                                  href="${window.location.pathname + window.location.search}#/${groupSlug}/${pageSlug}/"
                                >
                                  ${ServicePage.toTitleCase(menu.title)}
                                </a>
                              </li>
                            `;
                          })}
                        </ul>
                      </div>
                    ` : ''}
                  </li>
                `;
              })}
            </ul>

            <!-- Profile Area:  -->
            <div
              class="shrink-0 bg-[var(--md-sys-color-surfaceContainerLowest)] border-t border-[var(--md-sys-color-outlineVariant)]"
              style="padding: 16px;"
            >
              <div class="profile-area">
                <div style="display:flex; align-items:center; gap:12px;">
                  <!-- Avatar: w-10 h-10 rounded-full, primary bg, initials -->
                  <div
                    class="flex items-center justify-center bg-[var(--md-sys-color-primary)] text-[var(--md-sys-color-onPrimary)] font-semibold rounded-full flex-shrink-0 cursor-pointer hover:opacity-90 transition-opacity"
                    style="width:40px; height:40px; font-size:14px;"
                    title="User Profile"
                    @click=${this.toggleDropdown}
                  >
                    ${this.getProfileInitials()}
                  </div>

                  ${showSidebarExpanded ? html`
                    <div style="display:flex; flex-direction:column; min-width:0;">
                      <span class="text-[var(--md-sys-color-onSurface)] capitalize"
                            style="font-size:13px; font-weight:700; line-height:1.3; white-space:nowrap;">
                        ${this.gateway?._response?.profile?.first_name || ''}
                        ${this.gateway?._response?.profile?.last_name || ''}
                      </span>
                      <span class="text-[var(--md-sys-color-onSurfaceVariant)] truncate"
                            style="font-size:11px; max-width:120px;">
                        ${this.gateway?._response?.profile?.email || ''}
                      </span>
                    </div>
                  ` : ''}
                </div>

                ${showSidebarExpanded ? html`
                  <button
                    class="logout-icon-btn"
                    title="Logout"
                    @click=${(e: Event) => { e.stopPropagation(); this.logout(); }}
                  >
                    <span class="material-symbols-outlined" style="font-size: 20px;">logout</span>
                  </button>
                ` : ''}
              </div>
            </div>
          </nav>

          <!-- ===========================
               MAIN CONTENT
          =========================== -->
          <main class="flex-1 bg-[var(--md-sys-color-surfaceContainerLow)] min-w-0 main-scroll transition-colors duration-200"
                style="${this.gateway?._response.name == "Nenachat" ? "" : "padding: 24px;"}">
            <div style="display:flex; flex-wrap:wrap; margin:0; gap:0; ${this.gateway?._response.name == "Nenachat" ? "height:100%" : ""}">
              ${this.page?.pageInputGroups?.map((feed: any) => html`
                <div style="${this.gateway?._response.name == "Nenachat" ? "" :"padding: 0 8px 16px 8px;"}" class="${this._gridClasses(feed)}">
                
                  <form-render  style="${this.gateway?._response.name == "Nenachat" ? "height:100%;display: block;" : ""}" .feed="${feed}" .params=${this.parseParams()}></form-render>
                </div>
              `)}
            </div>
          </main>
        </div>

        <!-- ===========================
             DIALOG OVERLAY
        =========================== -->
        ${this.view === 'dialog' ? html`
          <div class="dialog-backdrop fixed inset-0 flex items-center justify-center z-50" @click=${this.closeDialog}>
            <div class="custom-dialog shadow-2xl max-w-[97vw] max-h-[90vh] min-w-[300px] min-h-[200px]"
                 @click=${(e: Event) => e.stopPropagation()}>
              <section-page
                  id="dialog"
                  .isSectionPage=${true}
                  .closeDialog=${this.closeDialog.bind(this)}
                  @view-list=${this._viewList}
                  @service-error=${(e: CustomEvent) => {
                    this._showSnackbar(e.detail?.error || 'Something went wrong', 'error');
                    this.closeDialog();
                  }}
                  .queue=${this.dialogServicesQueue}
                ></section-page>
            </div>
          </div>
        ` : ''}

        <!-- ===========================
             LOGOUT CONFIRMATION MODAL
        =========================== -->
        ${this.showLogoutConfirmation ? html`
          <div
            class="logout-modal-overlay fixed inset-0 flex items-center justify-center z-[10000]"
            style="background: rgba(0,0,0,0.5); backdrop-filter: blur(4px);"
            @click=${this.cancelLogout}
          >
            <div
              class="logout-modal bg-[var(--md-sys-color-surfaceContainerLowest)] border border-[var(--md-sys-color-outlineVariant)] shadow-xl w-full mx-4"
              style="max-width: 384px; border-radius: 8px; padding: 24px;"
              @click=${(e: Event) => e.stopPropagation()}
            >
              <!-- Header -->
              <div style="display:flex; align-items:center; gap:12px; margin-bottom:16px;">
                <div style="width:40px; height:40px; border-radius:50%; background:#FFF1F2; color:var(--md-sys-color-error); display:flex; align-items:center; justify-content:center; flex-shrink:0;">
                  <span class="material-symbols-outlined" style="font-size:22px;">logout</span>
                </div>
                <h3 class="text-[var(--md-sys-color-onSurface)] m-0"
                    style="font-size:20px; font-weight:700;">Confirm Logout</h3>
              </div>

              <!-- Body -->
              <p class="text-[var(--md-sys-color-onSurfaceVariant)] leading-relaxed"
                 style="font-size:14px; margin-bottom:32px; margin-top:0;">
                Are you sure you want to log out?
                
              </p>

              <!-- Actions -->
              <div style="display:flex; align-items:center; justify-content:flex-end; gap:12px;">
                <button
                  class="text-[var(--md-sys-color-onSurface)] bg-[var(--md-sys-color-secondaryContainer)] hover:opacity-90 transition-opacity focus:outline-none"
                  style="padding:10px 20px; border-radius:6px; font-size:14px; font-weight:500; border:none; cursor:pointer; font-family:inherit;"
                  @click=${this.cancelLogout}
                  ?disabled="${this.logoutLoading}"
                >
                  Cancel
                </button>
                <button
                  class="bg-[var(--md-sys-color-error)] text-[var(--md-sys-color-onError)] hover:opacity-90 transition-opacity focus:outline-none"
                  style="padding:10px 20px; border-radius:6px; font-size:14px; font-weight:500; border:none; cursor:pointer; font-family:inherit; min-width:90px;"
                  @click=${this.confirmLogout}
                  ?disabled="${this.logoutLoading}"
                >
                  ${this.logoutLoading ? html`
                    <span style="display:flex;align-items:center;justify-content:center;gap:6px;">
                      <svg class="spinner" style="width:14px;height:14px;" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Logging out...
                    </span>
                  ` : 'Logout'}
                </button>
              </div>
            </div>
          </div>
        ` : ''}

        <!-- ===========================
             SNACKBAR
        =========================== -->
        ${this._snackbarOpened ? html`
          <div class="snackbar ${this._snackbarContext} active">
            <div class="snackbar-header">
              ${this._snackbarContext === 'success' ? html`
                <svg class="snackbar-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/></svg>
              ` : this._snackbarContext === 'error' ? html`
                <svg class="snackbar-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z"/></svg>
              ` : this._snackbarContext === 'warning' ? html`
                <svg class="snackbar-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M1 21h22L12 2 1 21zm12-3h-2v-2h2v2zm0-4h-2v-4h2v4z"/></svg>
              ` : html`
                <svg class="snackbar-icon" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/></svg>
              `}
              <div class="snackbar-content">
                <div class="snackbar-title">${this._snackbarTitle}</div>
                <div class="snackbar-message">${this._snackbarMessage}</div>
              </div>
            </div>
            <button class="snackbar-close" @click=${this._closeSnackbar} aria-label="Close notification">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </button>
            <div class="snackbar-progress">
              <div class="snackbar-progress-bar" style="animation-duration: 5s;"></div>
            </div>
          </div>
        ` : ''}

      </div>
    `;
  }
}

customElements.define('service-page', ServicePage as any);